import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Icon } from '@components/icons';
import { socialMedia } from '@config';

const StyledFooter = styled.footer`
  margin-top: 150px;
  padding: 80px 50px;
  background: linear-gradient(180deg, rgba(100, 200, 100, 0.05) 0%, rgba(100, 200, 100, 0) 100%);
  border-top: 1px solid rgba(100, 200, 100, 0.1);

  @media (max-width: 768px) {
    padding: 60px 30px;
    margin-top: 100px;
  }

  @media (max-width: 480px) {
    padding: 40px 20px;
    margin-top: 60px;
  }
`;

const StyledFooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 60px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const StyledFooterSection = styled.div`
  h4 {
    font-size: var(--fz-md);
    color: var(--lightest-slate);
    margin-bottom: 24px;
    font-weight: 600;
    letter-spacing: 0.05em;
  }

  p {
    font-size: var(--fz-sm);
    color: var(--light-slate);
    line-height: 1.8;
    margin: 0;
  }
`;

const StyledSocialLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  gap: 20px;

  li {
    a {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border: 2px solid rgba(100, 200, 100, 0.3);
      border-radius: 4px;
      color: var(--light-slate);
      transition: all 0.3s ease;

      svg {
        width: 20px;
        height: 20px;
      }

      &:hover {
        border-color: var(--green);
        color: var(--green);
        background-color: rgba(100, 200, 100, 0.1);
        transform: translateY(-3px);
      }
    }
  }
`;

const StyledNewsletterForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;

  input {
    padding: 12px 16px;
    background-color: rgba(17, 34, 51, 0.7);
    border: 1px solid rgba(100, 200, 100, 0.3);
    border-radius: 4px;
    color: var(--lightest-slate);
    font-size: var(--fz-sm);
    font-family: var(--font-sans);
    transition: all 0.3s ease;

    &::placeholder {
      color: var(--light-slate);
    }

    &:focus {
      outline: none;
      border-color: var(--green);
      background-color: rgba(17, 34, 51, 0.9);
    }
  }

  button {
    padding: 12px 24px;
    background-color: var(--green);
    border: none;
    color: var(--dark-navy);
    border-radius: 4px;
    font-weight: 600;
    font-size: var(--fz-sm);
    letter-spacing: 0.05em;
    cursor: pointer;
    transition: all 0.3s ease;
    text-transform: uppercase;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(100, 200, 100, 0.2);
    }

    &:active {
      transform: scale(0.98);
    }
  }
`;

const StyledBottomBar = styled.div`
  margin-top: 60px;
  padding-top: 40px;
  border-top: 1px solid rgba(100, 200, 100, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }

  .copyright {
    font-size: var(--fz-xs);
    color: var(--light-slate);
    font-family: var(--font-mono);
  }

  .github-stats {
    display: flex;
    gap: 20px;
    font-size: var(--fz-xs);

    span {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      color: var(--light-slate);

      svg {
        width: 16px;
        height: 16px;
        color: var(--green);
      }
    }
  }
`;

const Footer = () => {
  const [githubInfo, setGitHubInfo] = useState({
    stars: null,
    forks: null,
  });

  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      return;
    }
    fetch('https://api.github.com/repos/SaidBenaissa/draatalents')
      .then(response => response.json())
      .then(json => {
        const { stargazers_count, forks_count } = json;
        setGitHubInfo({
          stars: stargazers_count,
          forks: forks_count,
        });
      })
      .catch(e => console.error(e));
  }, []);

  const handleNewsletterSubmit = e => {
    e.preventDefault();
    // Add your newsletter signup logic here
    setSubscribed(true);
    setEmail('');
    setTimeout(() => setSubscribed(false), 3000);
  };

  return (
    <StyledFooter>
      <StyledFooterContent>
        {/* Column 1: About */}
        <StyledFooterSection>
          <h4>About</h4>
          <p>
            Ph.D. Software Engineer with 10+ years of experience building impactful solutions across
            backend development, system architecture, and cloud infrastructure.
          </p>
        </StyledFooterSection>

        {/* Column 2: Quick Links */}
        <StyledFooterSection>
          <h4>Connect</h4>
          <StyledSocialLinks>
            {socialMedia &&
              socialMedia.map(({ name, url }, i) => (
                <li key={i}>
                  <a href={url} aria-label={name} target="_blank" rel="noopener noreferrer">
                    <Icon name={name} />
                  </a>
                </li>
              ))}
          </StyledSocialLinks>
        </StyledFooterSection>

        {/* Column 3: Newsletter */}
        <StyledFooterSection>
          <h4>Stay Updated</h4>
          <StyledNewsletterForm onSubmit={handleNewsletterSubmit}>
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
            <button type="submit">{subscribed ? 'Thank you!' : 'Subscribe'}</button>
          </StyledNewsletterForm>
        </StyledFooterSection>
      </StyledFooterContent>

      <StyledBottomBar>
        <div className="copyright">
          Copyright © {new Date().getFullYear()} Draa Talents. All rights reserved.
        </div>
        {githubInfo.stars && githubInfo.forks && (
          <div className="github-stats">
            <span>
              <Icon name="Star" />
              {githubInfo.stars.toLocaleString()}
            </span>
            <span>
              <Icon name="Fork" />
              {githubInfo.forks.toLocaleString()}
            </span>
          </div>
        )}
      </StyledBottomBar>
    </StyledFooter>
  );
};

Footer.propTypes = {
  githubInfo: PropTypes.object,
};

export default Footer;
